import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, LayoutGrid } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

// Level-1 responsive workspace nav. Width-aware: promotes/demotes lower-priority
// items into "More" instead of wrapping to multiple rows.
export function WorkspaceNav({ workspaces, currentId }) {
  const navigate = useNavigate();
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1280);
  useEffect(() => {
    const on = () => setW(window.innerWidth);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);

  const topZone = workspaces.filter((x) => (x.zone || "top") === "top");
  const moreZone = workspaces.filter((x) => x.zone === "more");

  // decide how many top items are inline vs pushed to More based on width
  let inline = topZone;
  let overflow = [];
  if (w < 1024) { inline = []; overflow = topZone; }         // tablet: switcher only
  else if (w < 1280) {
    inline = topZone.filter((x) => x.priority === "high");
    overflow = topZone.filter((x) => x.priority !== "high");
  }
  const moreItems = [...overflow, ...moreZone];

  const Btn = ({ x }) => {
    const Icon = x.icon;
    const active = x.id === currentId;
    return (
      <button onClick={() => navigate(x.route)} data-testid={`ws-${x.id}`}
        className={cn("flex items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
          active ? "bg-secondary text-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground")}>
        {Icon && <Icon className="h-4 w-4" />} {x.label}
      </button>
    );
  };

  return (
    <nav className="flex min-w-0 items-center gap-1" data-testid="workspace-nav">
      {w < 1024 ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-1.5 rounded-lg bg-secondary px-3 py-1.5 text-sm font-medium text-foreground" data-testid="workspace-switcher">
              <LayoutGrid className="h-4 w-4" />
              {workspaces.find((x) => x.id === currentId)?.label || "Workspace"}
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            {workspaces.map((x) => (
              <DropdownMenuItem key={x.id} onClick={() => navigate(x.route)} data-testid={`ws-switch-${x.id}`}>
                {x.icon && <x.icon className="mr-2 h-4 w-4" />} {x.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <>
          {inline.map((x) => <Btn key={x.id} x={x} />)}
          {moreItems.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1 whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground" data-testid="ws-more">
                  More <ChevronDown className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                {moreItems.map((x) => (
                  <DropdownMenuItem key={x.id} onClick={() => navigate(x.route)} data-testid={`ws-more-${x.id}`}>
                    {x.icon && <x.icon className="mr-2 h-4 w-4" />} {x.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </>
      )}
    </nav>
  );
}
